import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, Inbox } from 'lucide-react';
import { api } from '../../lib/api.js';
import { Badge, Button, Callout, Panel, Segmented, Spinner, type Tone } from '../../ui/index.js';
import { useConfigWrite } from './useConfigWrite.js';

/**
 * Mail tab → Reading this mailbox (059).
 *
 * Whether Tributary reads this desk's mailbox, and what it did with what it
 * read. The recent list is the point: every message is accounted for — filed
 * (with its ticket), declined and why, ignored as automated mail and by which
 * rule, or failed with a retry — so "did my email arrive?" has an answer on
 * screen.
 */

type Mode = 'off' | 'shadow' | 'on';

interface Payload {
  address: string | null;
  readingConfigured: boolean;
  /** The last check-in of a running worker; null when none has ever run. */
  workerSeenAt?: string | null;
  mode: Mode;
  lastPolledAt: string | null;
  lastOkAt: string | null;
  lastError: string | null;
  failures: number;
  last24h: Record<string, number>;
  recent: {
    id: string;
    receivedAt: string;
    from: string | null;
    subject: string | null;
    status: string;
    ticketNumber: number | null;
    summary: string;
  }[];
}

const MODES: { value: Mode; label: string; says: string }[] = [
  { value: 'off', label: 'Off', says: 'This mailbox is not read.' },
  {
    value: 'shadow',
    label: 'Shadow',
    says: 'Read and checked, and what would have happened is recorded below — but no ticket is opened and nobody is emailed.',
  },
  {
    value: 'on',
    label: 'On',
    says: 'Read every 15 seconds. Each email becomes a ticket, joins its ticket, is declined with your wording, or is ignored as automated mail.',
  },
];

const STATUS: Record<string, { label: string; tone: Tone }> = {
  filed: { label: 'filed', tone: 'ok' },
  declined: { label: 'declined', tone: 'warn' },
  rejected: { label: 'ignored', tone: 'neutral' },
  shadowed: { label: 'shadow', tone: 'info' },
  pending: { label: 'waiting', tone: 'neutral' },
  failed: { label: 'failed', tone: 'danger' },
};

const when = (iso: string) => new Date(iso).toLocaleString();

export function MailboxReadingPanel({ tenant }: { tenant: string }) {
  const write = useConfigWrite(tenant);
  const q = useQuery({
    queryKey: ['admin-mail-intake', tenant],
    queryFn: () => api.get<Payload>(`/api/v1/tenants/${tenant}/admin/mail-intake`),
    enabled: Boolean(tenant),
    refetchInterval: 20_000,
  });

  if (q.isLoading) return <Spinner />;
  if (q.error || !q.data) return <p className="text-xs text-ink-tertiary">Mailbox reading could not be loaded.</p>;
  const s = q.data;
  const current = MODES.find((m) => m.value === s.mode)!;
  // Two minutes: a worker checks in every 15 seconds.
  const noWorker = !s.workerSeenAt || Date.now() - new Date(s.workerSeenAt).getTime() > 120_000;
  const counts = ['filed', 'declined', 'rejected', 'shadowed', 'failed']
    .filter((k) => s.last24h[k])
    .map((k) => `${s.last24h[k]} ${STATUS[k]!.label}`);

  const setMode = (mode: Mode) =>
    void write.mutateAsync({
      method: 'put',
      path: `/api/v1/tenants/${tenant}/admin/mail-intake`,
      body: { mode },
      done: mode === 'off' ? 'Mailbox reading switched off' : mode === 'shadow' ? 'Mailbox reading in shadow' : 'Mailbox reading switched on',
    });

  return (
    <Panel
      title="Reading this mailbox"
      subtitle={s.address ? `Tributary reads ${s.address} through Microsoft Graph.` : 'Set the desk mailbox above first.'}
    >
      {!s.readingConfigured && (
        <Callout tone="warn" icon={<AlertTriangle />} className="mb-3">
          Mail reading is not set up for the platform yet, so nothing can be read. A platform administrator sets it on
          the Platform tab.
        </Callout>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Segmented
          value={s.mode}
          onChange={(v) => setMode(v)}
          options={MODES.map((m) => ({ value: m.value, label: m.label }))}
          aria-label="Mailbox reading"
        />
        {s.mode !== 'off' &&
          (s.lastError ? (
            <Badge tone="danger" icon={<AlertTriangle />}>
              not reading
            </Badge>
          ) : s.lastOkAt ? (
            <Badge tone="ok" icon={<Inbox />}>
              last read {when(s.lastOkAt)}
            </Badge>
          ) : noWorker ? (
            <Badge tone="warn" icon={<AlertTriangle />}>
              no worker running
            </Badge>
          ) : !s.readingConfigured ? (
            <Badge tone="warn" icon={<AlertTriangle />}>
              reading not set up
            </Badge>
          ) : (
            <Badge tone="neutral">starting</Badge>
          ))}
      </div>
      {s.mode !== 'off' && !s.lastOkAt && (noWorker || !s.readingConfigured) && (
        <Callout tone="warn" icon={<AlertTriangle />} className="mt-3">
          <p className="text-xs">
            {noWorker
              ? 'Nothing is reading this mailbox: no worker has checked in for over two minutes. It reads mail only while it runs — see Configuration → Platform → Worker.'
              : 'Mail reading has no application set up, so the worker cannot read this mailbox. Set it on Configuration → Platform → Mail reading.'}
          </p>
        </Callout>
      )}
      <p className="mt-2 text-2xs leading-relaxed text-ink-secondary">{current.says}</p>
      {s.mode !== 'off' && (
        <p className="mt-1 text-2xs text-ink-tertiary">
          Switching on reads from that moment — earlier mail in the mailbox is left alone.
        </p>
      )}

      {s.lastError && s.mode !== 'off' && (
        <Callout tone="danger" icon={<AlertTriangle />} className="mt-3">
          <p className="text-xs">{s.lastError}</p>
          <p className="mt-1 text-2xs text-ink-tertiary">
            {s.failures} failed read{s.failures === 1 ? '' : 's'} in a row. Reading keeps retrying, less often each
            time, until it works.
          </p>
        </Callout>
      )}

      <h4 className="mb-2 mt-4 text-xs font-semibold text-ink">
        Recent mail{counts.length ? <span className="font-normal text-ink-tertiary"> · last 24 hours: {counts.join(', ')}</span> : null}
      </h4>
      {s.recent.length === 0 ? (
        <p className="text-2xs text-ink-tertiary">Nothing read yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {s.recent.map((r) => (
            <li key={r.id} className="rounded-md border border-line-subtle p-2.5 text-2xs">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <Badge tone={STATUS[r.status]?.tone ?? 'neutral'}>{STATUS[r.status]?.label ?? r.status}</Badge>
                <span className="text-ink-tertiary">{when(r.receivedAt)}</span>
                <span className="font-medium text-ink">{r.from ?? '—'}</span>
                {r.subject && <span className="min-w-0 truncate text-ink-secondary">· {r.subject}</span>}
                {r.ticketNumber !== null && (
                  <Link className="ml-auto font-mono text-brand hover:underline" to={`/t/${tenant}/${r.ticketNumber}`}>
                    #{r.ticketNumber}
                  </Link>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-ink-secondary">
                <span>{r.summary}</span>
                {r.status === 'failed' && (
                  <Button
                    size="xs"
                    variant="outline"
                    loading={write.isPending}
                    onClick={() =>
                      void write.mutateAsync({
                        method: 'post',
                        path: `/api/v1/tenants/${tenant}/admin/mail-intake/retry`,
                        body: { id: r.id },
                        done: 'Queued to try again',
                      })
                    }
                  >
                    Retry
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
